'use client';
import { useState } from 'react';
import { ImageOff } from 'lucide-react';
import { useTranslation } from '../../lib/i18n';
import type { MessageBlockTone, MessageBlockView } from '../../lib/types';
import { Item, ItemContent, ItemDescription, ItemGroup, ItemMedia, ItemTitle } from '../../components/ui/shadcn/item';
import { LabelMark } from '../../components/ui/StatusMark';
import type { Tone } from '../../components/ui/tone';
import { ImageLightbox } from '../../components/ui/ImageLightbox';
import { Button } from '../../components/ui/Button';

/** A chip carries text and a tone, not a state or icon; the tone colours only its label dot. */
const CHIP_TONE: Record<MessageBlockTone, Tone> = {
  neutral: 'muted',
  positive: 'success',
  warning: 'warning',
  danger: 'danger',
  accent: 'accent',
};

/** The picture of one block: a small tile that opens the conversation's own image viewer. A picture the
 *  daemon no longer serves (swept, or a transcript read by someone who does not own it) says so in place
 *  instead of showing a broken glyph, and offers nothing to open. */
function BlockImage({ block }: { block: MessageBlockView & { image: NonNullable<MessageBlockView['image']> } }) {
  const { t } = useTranslation();
  const [failed, setFailed] = useState(false);
  const [open, setOpen] = useState(false);
  if (failed) {
    return (
      <ItemMedia variant="icon" className="size-14" title={t.brainChat.attachmentGone} data-testid="message-block-image-gone">
        <ImageOff aria-hidden />
        <span className="sr-only">{t.brainChat.attachmentGone}</span>
      </ItemMedia>
    );
  }
  return (
    <ItemMedia>
      <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={() => setOpen(true)}
        aria-label={`${t.brainChat.attachmentOpen}: ${block.title}`}
        className="h-auto size-14 overflow-hidden rounded-md border-hairline bg-image-ground p-0 hover:border-primary hover:bg-transparent focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
      >
        <img
          src={`/api${block.image.url}`}
          alt={block.title}
          loading="lazy"
          onError={() => setFailed(true)}
          className="size-full object-contain"
        />
      </Button>
      {open ? <ImageLightbox src={`/api${block.image.url}`} description={block.title} onClose={() => setOpen(false)} /> : null}
    </ItemMedia>
  );
}

/** The blocks a plugin tool attached to this reply, as a card grid under the message. Every string is a
 *  React text node: block text is plugin data and is never read as HTML or Markdown. A kind this build does
 *  not know (sent by a newer daemon) draws nothing. */
export function MessageBlocks({ blocks }: { blocks: MessageBlockView[] }) {
  const { t } = useTranslation();
  const items = blocks.filter((block) => block.kind === 'item');
  if (items.length === 0) return null;
  return (
    <div className="@container my-1.5 min-w-0">
      <ItemGroup aria-label={t.brainChat.messageBlocks} data-testid="message-blocks" className="grid grid-cols-1 gap-2 @lg:grid-cols-2 @3xl:grid-cols-3">
        {items.map((block) => (
          <Item key={block.key} variant="outline" size="sm" data-testid="message-block" className="flex-nowrap items-start">
            {block.image ? <BlockImage block={{ ...block, image: block.image }} /> : null}
            <ItemContent>
              <ItemTitle className="block w-full break-words">{block.title}</ItemTitle>
              {block.subtitle ? <ItemDescription className="break-words">{block.subtitle}</ItemDescription> : null}
              {block.chips?.length ? (
                <div className="flex min-w-0 flex-wrap gap-1 pt-0.5">
                  {block.chips.map((chip, index) => (
                    <LabelMark key={index} tone={CHIP_TONE[chip.tone]}>
                      <span className="truncate" title={chip.text}>{chip.text}</span>
                    </LabelMark>
                  ))}
                </div>
              ) : null}
            </ItemContent>
          </Item>
        ))}
      </ItemGroup>
    </div>
  );
}
